<template>
  <div class="w-full flex justify-center gap-2">
    <div class="glass-frosted rounded-8xl w-83 h-67.5 p-4">
      <div class="flex justify-between">
        <Button color="normal" class="w-12 h-6" size="sm" @click="emit('cancel')">
          {{ $t('common.backButton') }}
        </Button>
        <Button
          color="glass"
          class="w-12 h-6"
          size="sm"
          :active="isCanEnabled"
          :disabled="!isCanEnabled"
          @click="handleTakeEffect"
        >
          {{ $t('keys.mt.apply') }}
        </Button>
      </div>
      <div class="flex justify-center items-center gap-8 mt-2">
        <div
          v-for="mtType in mtTypes"
          :key="mtType.value"
          :class="`relative flex flex-col items-center ${selectedType === mtType.value ? 'text-text-important-default' : 'text-text-body-default'}`"
          @dragover="(event: DragEvent) => handleDragOver(mtType.value, event)"
          @dragleave="() => handleDragLeave(mtType.value)"
          @drop="(event: DragEvent) => handleDrop(mtType.value, event)"
        >
          <div
            :class="`flex justify-center items-center w-14 h-14 px-0.5 text-black-white rounded-xl border border-solid bg-key cursor-pointer transition-all hover:border-blue-light-hover ${getSelectionButtonBorderClass(mtType.value)}`"
            @click="() => (selectedType = mtType.value)"
          >
            <span class="w-full text-center text-l-medium leading-4 line-clamp-2">
              {{
                getLocalizedField(
                  currentSelection[mtType.value]?.alias || currentSelection[mtType.value]?.name,
                )
              }}
            </span>
          </div>
          <span class="text-m-medium text-black-white">{{ mtType.label }}</span>
          <Icon
            v-show="currentSelection[mtType.value]"
            icon="Function-Fill-Close"
            class="absolute -top-1 -right-1 w-4 h-4 text-brand-red cursor-pointer"
            @click.stop="clearSelection(mtType.value)"
          />
        </div>
      </div>
      <div class="mt-2">
        <span class="text-m-medium text-black-white">{{ $t('keys.mt.triggerTime') }}</span>
        <div class="flex items-center gap-2 mt-2">
          <Slider v-model="triggerInput" :min="10" :max="1000" :step="1" />
          <span class="text-s-medium text-black-white">{{ triggerInput[0] }}ms</span>
        </div>
        <div class="mt-2 text-text-glass-default text-s-medium">
          <span>{{ $t('keys.mt.description') }}</span>
          <div class="flex mt-2">
            <Icon icon="Function-FiII-Description" class="w-4 h-4 mt-0.5 mr-0.5" />
            <span>{{ $t('keys.mt.guide') }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="glass-frosted relative rounded-8xl w-250 h-67.5 p-4">
      <div class="p-2">
        <Keyboard
          key-class="flex items-center justify-center gap-1 bg-key text-black-white font-[Barlow] text-xs-semibold! whitespace-nowrap transition-colors cursor-pointer hover:text-text-glass-default active:bg-white active:text-brand-blue2 disabled:text-glass-disabled disabled:cursor-not-allowed disabled:pointer-events-none"
          :keyboard-config="keyboardConfig!"
          :native-event-names="sourceKeyboardEventNames"
          :gap="6"
          @key-event="handleSourceKeyEvent"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, inject, watch } from 'vue'
  import { Keyboard, Icon, Slider, Button, toast } from '@rapoo/element'
  import type { DualModeKey, MtSlotInfo } from '@rapoo/device-sdk'
  import { useLocaleStore } from '@/stores'
  import { getDragData, setDragData, DragType } from '@/utils/dnd'
  import { useDragImage } from '../../../composables/useDragImage'
  import { fullSize108 } from '@/utils/fullSize108'
  import {
    KEYCODE_KEYWORDS_MAP,
    getLocalizedField,
    getKeyConfigByTypeCode,
    buildKeySettingFromPayload,
  } from '../../../utils/keyMapSettings'
  import {
    GCOME_MATRIX_TYPE,
    buildMtSlotFromKeyConfigs,
    gcomeTripleToKeyConfig,
    resolveMtDelayMs,
  } from '../../../utils/advancedKeyMapSettings'
  import {
    SelectedKeySymbol,
    KeySettingsSymbol,
    KeyLayerSymbol,
    type KeyChangePayload,
  } from '../../../types'
  import { findSettingByTargetKey, parseNumber } from '../../../utils/keyIdentity'
  import { useAdvancedKeyCommit } from '../composables/useAdvancedKeyCommit'

  import type {
    KeyboardConfig,
    KeyEventName,
    KeyboardKeyEventPayload,
    KeyConfig,
  } from '@rapoo/element'

  const emit = defineEmits<{
    success: []
    cancel: []
  }>()

  const { t } = useLocaleStore()
  const { commitAdvancedKey } = useAdvancedKeyCommit()

  type SelectionType = 'click' | 'press'

  const SINGLE_KEY_META_TYPE = 0x01

  const selectedKey = inject(SelectedKeySymbol)
  const keySettings = inject(KeySettingsSymbol)
  const currentLayer = inject(KeyLayerSymbol)

  const mtTypes = computed<{ label: string; value: SelectionType }[]>(() => [
    {
      label: t('keys.mt.click'),
      value: 'click',
    },
    {
      label: t('keys.mt.longPress'),
      value: 'press',
    },
  ])

  const sourceKeyboardEventNames: KeyEventName[] = ['click', 'dragstart']

  const keyboardConfig = computed<KeyboardConfig | null>(() => {
    return {
      ...fullSize108,
      keys: fullSize108.keys.map((row) =>
        row.map((key) => {
          const { w, h, x, y, keywords, ...rest } = key
          const config: KeyConfig = KEYCODE_KEYWORDS_MAP.get(keywords)!

          return {
            ...rest,
            ...config,
            rect: {
              w,
              h,
              x,
              y,
            },
          }
        }),
      ),
    }
  })

  const APPLY_TOAST_ID = 'advanced-mt-apply'

  const triggerInput = ref([1000])
  const selectedType = ref<SelectionType>('click')
  const dragOverType = ref<SelectionType | null>(null)
  const isSubmitting = ref(false)

  const currentSelection = ref<Record<SelectionType, KeyConfig | null>>({
    click: null,
    press: null,
  })

  const isCanEnabled = computed(() =>
    Boolean(
      selectedKey?.value &&
      currentSelection.value.click &&
      currentSelection.value.press &&
      !isSubmitting.value,
    ),
  )

  const restoreSelectionFromMt = (mt: MtSlotInfo, delayMs?: number) => {
    currentSelection.value = {
      click: gcomeTripleToKeyConfig(mt.tap),
      press: gcomeTripleToKeyConfig(mt.hold),
    }
    if (delayMs != null) {
      triggerInput.value = [delayMs]
    }
  }

  const restoreSelectionFromDualModeKey = (dualModeKey: DualModeKey) => {
    currentSelection.value = {
      click: getKeyConfigByTypeCode(SINGLE_KEY_META_TYPE, dualModeKey.clickKeyCode) ?? null,
      press: getKeyConfigByTypeCode(SINGLE_KEY_META_TYPE, dualModeKey.longPressKeyCode) ?? null,
    }
    if (dualModeKey.time != null) {
      triggerInput.value = [dualModeKey.time]
    }
  }

  watch(
    () => selectedKey?.value,
    (key) => {
      if (!key || !keySettings?.value) return

      const setting = findSettingByTargetKey(keySettings.value, key)
      if (setting?.keyType !== 'MT') return

      if (setting.raw?.mt) {
        restoreSelectionFromMt(setting.raw.mt as MtSlotInfo, resolveMtDelayMs(setting))
        return
      }

      if (setting.raw?.dualModeKey) {
        restoreSelectionFromDualModeKey(setting.raw.dualModeKey as DualModeKey)
      }
    },
    { immediate: true },
  )

  watch(isCanEnabled, (enabled, wasEnabled) => {
    if (enabled && !wasEnabled) {
      toast.info(t('toast.keys.mtApply'), { id: APPLY_TOAST_ID })
    }
  })

  const { setDragImage } = useDragImage()

  const getSelectionButtonBorderClass = (type: SelectionType) => {
    if (dragOverType.value === type || selectedType.value === type) return 'border-brand-blue1'
    return 'border-transparent'
  }

  const clearSelection = (type: SelectionType) => {
    currentSelection.value[type] = null
  }

  const setSelection = (type: SelectionType, item: KeyConfig) => {
    currentSelection.value[type] = item
    const otherType: SelectionType = type === 'click' ? 'press' : 'click'
    // 当前框填完后自动激活未填的框；若这是最后一个被填充的框则不切换
    if (!currentSelection.value[otherType]) {
      selectedType.value = otherType
    }
  }

  const handleDragOver = (type: SelectionType, event: DragEvent) => {
    event.preventDefault()
    dragOverType.value = type

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy'
    }
  }

  const handleDragLeave = (type: SelectionType) => {
    if (dragOverType.value === type) {
      dragOverType.value = null
    }
  }

  const handleDrop = (type: SelectionType, event: DragEvent) => {
    event.preventDefault()
    dragOverType.value = null

    const dataTransfer = event.dataTransfer
    if (!dataTransfer) return
    const draggedData = getDragData(dataTransfer, DragType.KEY_MAPPING)

    if (draggedData) {
      setSelection(type, draggedData as KeyConfig)
    }
  }

  const handleSourceKeyEvent = (ctx: KeyboardKeyEventPayload) => {
    const { data, eventName, nativeEvent } = ctx

    if (eventName === 'click') {
      setSelection(selectedType.value, data)
      return
    }

    if (eventName === 'dragstart') {
      const event = nativeEvent as DragEvent
      const dataTransfer = event.dataTransfer
      if (!dataTransfer) return

      const dragData: KeyConfig = data
      setDragData(dataTransfer, DragType.KEY_MAPPING, dragData as any)
      setDragImage(event, {
        text: getLocalizedField(data.alias || data.name),
      })
    }
  }

  const resolveTargetIndex = () => {
    if (!selectedKey?.value || !keySettings?.value) return undefined
    const existingSetting = findSettingByTargetKey(keySettings.value, selectedKey.value)
    return parseNumber(existingSetting?.raw?.index) ?? parseNumber(selectedKey.value.key)
  }

  const handleTakeEffect = async () => {
    if (!selectedKey?.value) {
      toast.info(t('toast.keys.advancedSelectKey'))
      return
    }

    if (!isCanEnabled.value) {
      toast.info(t('toast.keys.mtSelect'))
      return
    }

    const click = currentSelection.value.click
    const press = currentSelection.value.press
    if (!click || !press) return

    const targetIndex = resolveTargetIndex()
    if (targetIndex == null) return

    const triggerMs = triggerInput.value[0] ?? 1000
    const delaySteps = Math.max(0, Math.min(255, Math.round(triggerMs / 10)))
    const mt = buildMtSlotFromKeyConfigs({ click, press })

    const payload: KeyChangePayload = {
      keywords: selectedKey.value.keywords,
      keyType: 'MT',
      target: null,
      raw: {
        type: GCOME_MATRIX_TYPE.MT,
        mt,
        mtDelaySteps: delaySteps,
        clickKeyLabel: getLocalizedField(click.alias || click.name),
        pressKeyLabel: getLocalizedField(press.alias || press.name),
      },
    }

    const existingSetting = keySettings?.value
      ? findSettingByTargetKey(keySettings.value, selectedKey.value)
      : undefined

    const newSetting = buildKeySettingFromPayload({
      sourceData: payload,
      index: targetIndex,
    })
    if (!newSetting) return

    if (existingSetting?.raw?.mtIndex != null) {
      newSetting.raw.mtIndex = existingSetting.raw.mtIndex
    }

    let didSucceed = false
    isSubmitting.value = true
    toast.dismiss(APPLY_TOAST_ID)
    try {
      const committed = await commitAdvancedKey({
        setting: newSetting,
        existingSetting,
        isFn: currentLayer?.value === 'fn',
      })
      if (!committed) return

      toast.dismiss('keyboard')
      emit('success')
      didSucceed = true
    } finally {
      // 成功后父组件会切回列表；这里复位会让 isCanEnabled 再变 true，重复弹出“请点击生效”
      if (!didSucceed) isSubmitting.value = false
    }
  }
</script>

<style scoped></style>
